import { useState } from "react";

// Deterministic color palette from team name
const PALETTE = [
  "#D4AF37", // gold
  "#10B981", // emerald
  "#3B82F6", // blue
  "#A78BFA", // violet
  "#F97316", // orange
  "#EC4899", // pink
  "#EF4444", // red
  "#14B8A6", // teal
];

function teamColor(name = "") {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return PALETTE[Math.abs(hash) % PALETTE.length];
}

function teamInitials(name = "") {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

/**
 * TeamLogo — smart team logo with graceful fallback to colored initials.
 *
 * Props:
 *  - logoUrl   : URL of the team logo image (ESPN CDN, football-data crest, etc.)
 *  - teamName  : Team name (used for initials fallback and deterministic color)
 *  - size      : pixel size (default 36)
 *  - className : extra CSS classes
 *  - rounded   : "full" | "md" | "lg" (default "md" — softly rounded square)
 */
export default function TeamLogo({ logoUrl, teamName = "", size = 36, className = "", rounded = "md" }) {
  const [imgError, setImgError] = useState(false);

  const initials = teamInitials(teamName);
  const color    = teamColor(teamName);
  const radius   = rounded === "full" ? "50%" : rounded === "lg" ? size * 0.22 : size * 0.16;

  const sharedStyle = {
    width:      size,
    height:     size,
    flexShrink: 0,
    borderRadius: radius,
  };

  if (logoUrl && !imgError) {
    return (
      <img
        src={logoUrl}
        alt={teamName}
        className={className}
        style={{
          ...sharedStyle,
          objectFit: "contain",
          background: "rgba(255,255,255,0.04)",
          padding: size > 24 ? 2 : 1,
        }}
        onError={() => setImgError(true)}
      />
    );
  }

  // Fallback: colored initial badge
  return (
    <div
      className={className}
      aria-label={teamName}
      style={{
        ...sharedStyle,
        background: `${color}18`,
        border:     `1px solid ${color}35`,
        display:    "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize:   Math.max(9, size * 0.33),
        fontWeight: 900,
        color:      color,
        letterSpacing: "-0.5px",
        userSelect: "none",
      }}
    >
      {initials}
    </div>
  );
}
